import {generateCallback, implementCallback} from '../util/appCallback';
import util, {noop} from '../util/common';
import fn from '../util/fn';
import invoking from '../util/invoking';
import isEqual from 'lodash.isequal';
import {resolveColorString} from '../util/color';

// 延迟处理
function delaySuccessEvent(option, res) {
    return () => {
        option.success();
        // 业务未执行完，可能被中断，延迟处理
        setTimeout(() => {
            res.next();
        }, 100);
    };
}

// 存放颜色公共状态
const colorState = {};

// 颜色设置 invoke，用于存放颜色公共状态，防止颜色设置重复调用
function colorSettingInvoke(options) {
    const opt = options.option;
    if (isEqual(options.data, colorState[options.action])) {
        // 如果重复设置，就当是设置成功
        implementCallback({code: 0}, opt);
        return;
    }

    const originalSuccess = typeof opt.success === 'function' ? opt.success : noop;

    opt.success = function (...args) {
        colorState[options.action] = options.data;
        return originalSuccess(...args);
    };

    invoking(options);
}

// 设置导航标题
export function setTitle(option = {}) {
    // 特殊的空白字符，用于空标题实现
    const title = option.title || '‏‏‎ ‎';
    window.document.title = title;
    if (this.setNavTitle) {
        util.waitTimeout(50).then(() => {
            // 针对 ios，安卓不需要
            this.setNavTitle({
                title: title
            });
            implementCallback({code: 0}, option);
        });
    }
}

// 打开窗口
export function openWindow(option) {
    if (this.openNewWeb) {
        if (Object.prototype.toString.call(option.close) === '[object Function]') {
            window.Mama.newWebCloseResult = option.close;
        }
        this.openNewWeb({
            url: util.formatURL(option.url) || window.location.href,
            title: option.title || window.document.title,
            mode: option.mode || 0,
            action: option.action || ''
        });
    }
}

// 关闭窗口
export function closeWindow(option) {
    if (this.webViewFinish) {
        this.webViewFinish();
        implementCallback({code: 0}, option);
    }
}

// 监听后退、关闭接口
export function popState(option = {}) {
    const op = Number(option.opt);
    if (op !== 1 && op !== 2) {
        return;
    }
    window.Mama.popPreviousStep.isBan = 1;
    window.Mama.popPreviousStep.opt[option.opt] = true;
    const globalMethodName = generateCallback(option, opt => {
        const resolvedOpt = Number(opt);
        return {
            code: 0,
            data: {
                opt: resolvedOpt,
                next: resolvedOpt === 2 ? window.MJSSDK.webViewFinish : window.MJSSDK.webViewGoBack
            }
        };
    });
    window.Mama.popPreviousStep.callback[option.opt] = window[globalMethodName];
}

// 取消监听后退、关闭接口
export function cancelPopState(option = {}) {
    if (option.opt) {
        window.Mama.popPreviousStep.opt[option.opt] = false;
        window.Mama.popPreviousStep.callback[option.opt] = null;
    } else {
        window.Mama.popPreviousStep.isBan = 0;
        window.Mama.popPreviousStep.opt[1] = false;
        window.Mama.popPreviousStep.opt[2] = false;
        window.Mama.popPreviousStep.callback = function () {
            // 声明空函数
        };
    }
}

// 阻止后退，弹出二次确认框
export function closeWebViewConfirm(option = {}) {
    popState({
        opt: 2,
        success(res) {
            const successEvent = typeof option.success === 'function' ? delaySuccessEvent(option, res) : res.next;
            fn.confirm({
                title: option.title || '温馨提示',
                desc: option.desc || '是否确认关闭窗口？',
                className: option.className || '',
                btn: option.btn || ['确认', '取消'],
                yes: successEvent,
                no:
                    option.cancel ||
                    function () {
                        // 声明空函数
                    }
            });
        }
    });
}

// 阻止关闭，弹出二次确认框
export function backWebViewConfirm(option = {}) {
    popState({
        opt: 1,
        success(res) {
            const successEvent = typeof option.success === 'function' ? delaySuccessEvent(option, res) : res.next;
            fn.confirm({
                title: option.title || '温馨提示',
                desc: option.desc || '是否确认返回？',
                className: option.className || '',
                btn: option.btn || ['确认', '取消'],
                yes: successEvent,
                no:
                    option.cancel ||
                    function () {
                        // 声明空函数
                    }
            });
        }
    });
}

const changePullRefresh = function (option, isOpen) {
    if (this.isOpenDragRefresh) {
        this.isOpenDragRefresh({
            isOpen: isOpen
        });
        implementCallback({code: 0}, option);
    }
};

// 启用下拉刷新
export function enabledPullRefresh(option) {
    changePullRefresh.call(this, option, 1);
}

// 禁用下拉刷新
export function disabledPullRefresh(option) {
    changePullRefresh.call(this, option, 0);
}

/**
 * 修改导航栏标题颜色
 * @param {string} option.color 颜色值，HEX 格式，例如：#ffffff
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavTitleColor(option = {}) {
    colorSettingInvoke({
        action: 'changeNavTitleColor',
        data: {
            // 标题颜色只允许 rgb
            color: resolveColorString.call(this, option.color).slice(0, 7)
        },
        option
    });
}

/**
 * 修改导航栏背景颜色
 * @param {string} option.color 颜色值，HEX 格式，例如：#ffffff
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavBGColor(option = {}) {
    colorSettingInvoke({
        action: 'changeNavBGColor',
        data: {
            color: resolveColorString.call(this, option.color)
        },
        option
    });
}

/**
 * 修改导航栏分享按钮
 * @param {string} option.imgUrl 分享图片链接
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavShareButtonColor(option = {}) {
    // 如果重新设置了图标则保存在全局
    this.customShareBtn = util.formatURL(option.imgUrl);
    colorSettingInvoke({
        action: 'changeNavShareButtonColor',
        data: {
            imgUrl: util.formatURL(option.imgUrl)
        },
        option
    });
}

/**
 * 修改导航栏返回按钮，由于安卓和 iOS 端采用不同方式实现的返回按钮（一个用图片，一个用代码），所以这里需要同时传颜色和图片
 * @param {string} option.color 颜色值，HEX 格式，例如：#ffffff
 * @param {string} option.imgUrl icon 图片链接
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavBackButtonColor(option = {}) {
    colorSettingInvoke({
        action: 'changeNavBackButtonColor',
        data: {
            color: resolveColorString.call(this, option.color),
            imgUrl: util.formatURL(option.imgUrl)
        },
        option
    });
}

/**
 * 修改导航栏关闭按钮，由于安卓和 iOS 端采用不同方式实现的返回按钮（一个用图片，一个用代码），所以这里需要同时传颜色和图片
 * @param {string} option.color 颜色值，HEX 格式，例如：#ffffff
 * @param {string} option.imgUrl icon 图片链接
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavCloseButtonColor(option = {}) {
    colorSettingInvoke({
        action: 'changeNavCloseButtonColor',
        data: {
            color: resolveColorString.call(this, option.color),
            imgUrl: util.formatURL(option.imgUrl)
        },
        option
    });
}

/**
 * 统一修改导航栏元素
 * @param {{
 *      titleColor?: string,
 *      bgColor?: string,
 *      shareButtonImg?: string,
 *      closeButtonColor?: string,
 *      closeButtonImg?: string,
 *      backButtonColor?: string,
 *      backButtonImg?: string,
 *  }} options - options.titleColor 标题字体颜色
 *             - options.bgColor 导航栏背景颜色
 *             - options.shareButtonImg 分享按钮图片
 *             - options.closeButtonColor 关闭按钮颜色（苹果）
 *             - options.closeButtonImg 关闭按钮图片 img（安卓）
 *             - options.backButtonColor 后退按钮颜色（苹果）
 *             - options.backButtonImg 后退按钮图片（安卓）
 */
export function setNavElementStyle(options = {}) {
    if (options.bgColor) {
        setNavBGColor.call(this, {color: options.bgColor});
    }

    if (options.titleColor) {
        setNavTitleColor.call(this, {color: options.titleColor});
    }

    if (options.shareButtonImg) {
        setNavShareButtonColor.call(this, {
            imgUrl: options.shareButtonImg
        });
    }

    if (options.closeButtonColor && options.closeButtonImg) {
        setNavCloseButtonColor.call(this, {
            color: options.closeButtonColor,
            imgUrl: options.closeButtonImg
        });
    }

    if (options.backButtonColor && options.backButtonImg) {
        setNavBackButtonColor.call(this, {
            color: options.backButtonColor,
            imgUrl: options.backButtonImg
        });
    }
}
/**
 * 修改状态栏主题
 * 应用场景：此功能搭配webview全屏模式使用
 * @param {string} type 亮色(light)/深色(dark)主题
 */
export function setNavTheme(type) {
    if (type === 'dark') {
        this.setNavElementStyle({
            titleColor: '#333333', // 标题字体颜色
            bgColor: 'transparent', // '导航栏背景颜色 写死透明
            backButtonColor: '#333333', // '后退按钮颜色（苹果）
            backButtonImg: 'https://static-files.cdnmama.com/common/theme-dark-back.png', // 后退按钮图片链接（安卓）
            closeButtonColor: '#333333', // 关闭按钮颜色（苹果）
            closeButtonImg: 'https://static-files.cdnmama.com/common/theme-dark-close.png', // 关闭按钮图片链接（安卓）
            shareButtonImg: 'https://static-files.cdnmama.com/common/theme-dark-share.png' // 分享按钮图片
        });
    }
    if (type === 'light') {
        this.setNavElementStyle({
            titleColor: '#ffffff', // 标题字体颜色
            bgColor: 'transparent', // '导航栏背景颜色 写死透明
            backButtonColor: '#ffffff', // '后退按钮颜色（苹果）
            backButtonImg: 'https://static-files.cdnmama.com/common/theme-light-back.png', // 后退按钮图片链接（安卓）
            closeButtonColor: '#ffffff', // 关闭按钮颜色（苹果）
            closeButtonImg: 'https://static-files.cdnmama.com/common/theme-light-close.png', // 关闭按钮图片链接（安卓）
            shareButtonImg: 'https://static-files.cdnmama.com/common/theme-light-share.png' // 分享按钮图片
        });
    }
}
/**
 * 设置导航栏关闭按钮的显示与隐藏
 * @param {boolean} option.isShow 是否显示
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setCloseButton(option = {}) {
    invoking({
        action: 'setCloseBtnStatus',
        data: {
            isShow: option.isShow
        },
        option
    });
}

/**
 * 设置导航栏的显示与隐藏
 * @param {boolean} option.isShow 是否显示
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setNavVisibility(option) {
    invoking({
        action: 'changeNavBarVisibility',
        data: {
            display: option.isShow ? 1 : 0
        },
        option
    });
}

/**
 * 设置 webview 的全屏显示模式
 * @param {boolean} option.active 是否启用
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function setFullscreenWebview(option) {
    invoking({
        action: 'changeFullScreenMode',
        data: {
            open: option.active ? 1 : 0
        },
        option
    });
}

/**
 * 获取导航栏和状态栏的高度信息
 * @param {boolean} option.active 是否启用
 * @param {Object} option 其他配置，option.success-成功回调函数、option.fail 失败回调函数、option.cancel 取消回调函数
 */
export function getStatusBarInfo(option) {
    invoking({
        action: 'getStatusBarInfo',
        option: {
            ...option,
            success: res => {
                res.titleHeight = Number(res.titleHeight);
                res.statusBarHeight = Number(res.statusBarHeight);
                if (option?.success) {
                    option?.success(res);
                }
            }
        }
    });
}

export default {
    setTitle,
    openWindow,
    closeWindow,
    popState,
    cancelPopState,
    setFullscreenWebview,
    getStatusBarInfo,
    closeWebViewConfirm,
    backWebViewConfirm,
    enabledPullRefresh,
    disabledPullRefresh,
    setNavTitleColor,
    setNavBGColor,
    setNavTheme,
    setNavShareButtonColor,
    setNavCloseButtonColor,
    setNavBackButtonColor,
    setNavElementStyle,
    setNavVisibility,
    setCloseButton
};
